import {
    FETCH_WITHDRAWSREFER,
    FETCH_WITHDRAWSREFER_SUCCESS,
    FETCH_WITHDRAWSREFER_FAILED,
    EDIT_WITHDRAWSREFER
  } from "../store/types";
  import { firebase } from '../config/configureFirebase';
  import { onValue, child, update, off, push, get } from "firebase/database";
  import {
    resolveEarningsCap,
    getMonthKey,
    getWithdrawPaydayKey,
    getPaydayWithdrawCap,
    getPaydayWithdrawUsed,
    getMonthlyWithdrawUsed,
    getBogotaDay,
    formatWithdrawPaydays,
    formatCop,
    isLeaderDriver,
    MIN_REFERRAL_WITHDRAW,
  } from "../other/networkSlots";

  const VERDICT_TIMEOUT_MS = 20000;
  
  const PROCESSED_WITHDRAWS_LIMIT = 100;
  let fetchTimeoutId = null;

  export const fetchWithdrawsRefer = ()=>(dispatch) => {
    const {
        withdrawComRef
    } = firebase;
    
    // CORRECCIÓN: Limpiar listener anterior antes de crear uno nuevo
    off(withdrawComRef);
    clearTimeout(fetchTimeoutId);

    dispatch({
      type: FETCH_WITHDRAWSREFER,
      payload: null
    });
    
    // Timeout de seguridad: si el listener no responde en 10 segundos, enviar array vacío
    const timeoutId = setTimeout(() => {
      console.warn('Timeout: El listener de withdraws no respondió en 10 segundos');
      dispatch({
        type: FETCH_WITHDRAWSREFER_SUCCESS,
        payload: []
      });
    }, 10000);
    fetchTimeoutId = timeoutId;
    
    onValue(withdrawComRef, snapshot => {
      // Cancelar timeout si el listener responde
      clearTimeout(timeoutId);
      
      // Verificar si el snapshot tiene datos válidos
      const snapshotData = snapshot.val();
      if (snapshotData !== null && snapshotData !== undefined) {
        const data = snapshotData;
        const arr = Object.keys(data).map(i => {
          data[i].id = i
          return data[i]
        });
        // Los pendientes nunca se recortan: el admin debe ver todos los que faltan por pagar.
        const byDateDesc = (a, b) => (Number(b.date) || 0) - (Number(a.date) || 0);
        const isClosed = (w) => w.processed || w.rejected;
        const pending = arr.filter((w) => !isClosed(w)).sort(byDateDesc);
        const processed = arr
          .filter(isClosed)
          .sort(byDateDesc)
          .slice(0, PROCESSED_WITHDRAWS_LIMIT);
        const limitedArr = [...pending, ...processed];
        dispatch({
          type: FETCH_WITHDRAWSREFER_SUCCESS,
          payload: limitedArr
        });
      } else {
        // Si no hay datos, enviar un array vacío en lugar de FAILED para que no se quede en loading
        dispatch({
          type: FETCH_WITHDRAWSREFER_SUCCESS,
          payload: []
        });
      }
    }, (error) => {
      // Cancelar timeout si hay error
      clearTimeout(timeoutId);
      
      // Manejar errores del listener
      let errorMessage = "Error al cargar retiros de comisión.";
      if (error.code === 'permission-denied') {
        errorMessage = "No tienes permisos para ver los retiros de comisión. Contacta al administrador.";
        console.error('Error de permisos en withdrawsComi:', error);
      } else {
        console.error('Error en listener de withdraws:', error);
      }
      
      dispatch({
        type: FETCH_WITHDRAWSREFER_FAILED,
        payload: errorMessage
      });
    });
  };

  // OPTIMIZACIÓN: Función para limpiar listeners de withdraws
  export const stopFetchWithdrawsRefer = ()=>(dispatch) => {
    const {
        withdrawComRef
    } = firebase;
    
    dispatch({
      type: 'STOP_FETCH_WITHDRAWSREFER',
      payload: null
    });
    
    // Limpiar listener
    off(withdrawComRef);
    clearTimeout(fetchTimeoutId);
  };
  
  export const completeWithdrawRefer = (entry) => (dispatch) => {
  
    const {
        withdrawComRef
    } = firebase;
    
    dispatch({
      type: EDIT_WITHDRAWSREFER,
      payload: entry
    });
    // Solo se tocan estos campos: reescribir la fila completa guardaría `tableData` de material-table en la BD.
    return update(child(withdrawComRef, entry.id), { processed: true, processDate: new Date().getTime() });
  };

  /**
   * Admin rechaza una solicitud pendiente. Si el servidor la había validado (saldo descontado),
   * la Cloud Function devuelve el saldo al bono de red y notifica al conductor.
   */
  export const rejectWithdrawRefer = (entry, message) => () => {
    const { withdrawComRef, auth } = firebase;
    return update(child(withdrawComRef, entry.id), {
      rejected: true,
      rejectReason: 'admin',
      rejectMessage: String(message || '').trim().slice(0, 300),
      rejectDate: new Date().getTime(),
      rejectedBy: auth.currentUser ? auth.currentUser.uid : null,
    });
  };

  /** Datos actuales del conductor para que el admin verifique un retiro. */
  export const getWithdrawUserContext = async (uid) => {
    const { singleUserRef } = firebase;
    if (!uid) return null;
    const user = (await get(singleUserRef(uid))).val();
    if (!user) return null;
    const type = isLeaderDriver(user, uid) ? 'Líder' : 'Estándar';
    return {
      rows: [
        { label: 'Tipo', value: user.cityName ? `${type} · ${user.cityName}` : type },
        { label: 'Saldo de bono actual', value: formatCop(parseFloat(user.walletReferral) || 0) },
        {
          label: 'Retirado este mes',
          value: `${formatCop(getMonthlyWithdrawUsed(user, getMonthKey()))} de ${formatCop(resolveEarningsCap(user, uid))}`,
        },
        { label: 'Cupo por día de pago', value: formatCop(getPaydayWithdrawCap(user, uid)) },
      ],
    };
  };

  const waitForVerdict = (ref) =>
    new Promise((resolve) => {
      let unsubscribe = null;
      const timer = setTimeout(() => {
        if (unsubscribe) unsubscribe();
        resolve(null);
      }, VERDICT_TIMEOUT_MS);
      unsubscribe = onValue(
        ref,
        (snap) => {
          const w = snap.val();
          if (w && (w.validated || w.rejected)) {
            clearTimeout(timer);
            if (unsubscribe) unsubscribe();
            resolve(w);
          }
        },
        () => {
          clearTimeout(timer);
          resolve(null);
        }
      );
    });

  /**
   * Retiro de bono de red: solo días 15 y 30 (Bogotá).
   * Tope por día = mitad del techo mensual (líder 25M+25M / estándar 15M+15M).
   * El cliente solo registra la solicitud; la Cloud Function `withdrawComiCreate`
   * valida y descuenta el saldo.
   */
  export const withdrawReferral = (profile, amount) => async () => {
    const { withdrawComRef, singleUserRef } = firebase;
    const uid = profile?.uid || profile?.id;
    const requested = Math.round(parseFloat(amount) || 0);
    if (!uid || requested <= 0) {
      return { ok: false, error: 'invalid_amount' };
    }
    if (requested < MIN_REFERRAL_WITHDRAW) {
      return {
        ok: false,
        error: 'below_minimum',
        message: `El retiro mínimo es ${formatCop(MIN_REFERRAL_WITHDRAW)}.`,
      };
    }

    const paydayKey = getWithdrawPaydayKey();
    const day = getBogotaDay();
    if (!paydayKey) {
      return {
        ok: false,
        error: 'not_payday',
        message: `Los retiros de bono solo están disponibles los días ${formatWithdrawPaydays()} de este mes.`,
      };
    }

    const snap = await get(singleUserRef(uid));
    const user = snap.val();
    if (!user) return { ok: false, error: 'user_not_found' };

    const balance = parseFloat(user.walletReferral) || 0;
    if (requested > balance) {
      return { ok: false, error: 'insufficient_balance' };
    }

    const monthlyCap = resolveEarningsCap(user, uid);
    const paydayCap = getPaydayWithdrawCap(user, uid);
    const usedPayday = getPaydayWithdrawUsed(user, paydayKey);
    const room = Math.max(0, paydayCap - usedPayday);
    if (requested > room) {
      return {
        ok: false,
        error: 'payday_withdraw_cap',
        message: `Tope del día ${day}: ${formatCop(paydayCap)} (mitad de ${formatCop(monthlyCap)}/mes). Ya retiraste ${formatCop(usedPayday)}; disponible hoy ${formatCop(room)}.`,
        room,
        cap: paydayCap,
        monthlyCap,
        usedPayday,
      };
    }

    let newRef;
    try {
      newRef = await push(withdrawComRef, {
        uid,
        amount: requested,
        date: new Date().getTime(),
        processed: false,
        type: 'referral_bonus',
      });
    } catch (error) {
      return {
        ok: false,
        error: 'withdraw_request_failed',
        message: 'No pudimos registrar tu solicitud de retiro. Tu saldo no fue descontado; intenta de nuevo más tarde.',
      };
    }

    const verdict = await waitForVerdict(newRef);
    if (!verdict) {
      return {
        ok: true,
        pending: true,
        message: 'Recibimos tu solicitud. La estamos validando; revisa tu saldo en unos minutos.',
      };
    }
    if (verdict.rejected) {
      return {
        ok: false,
        error: verdict.rejectReason || 'withdraw_rejected',
        message: verdict.rejectMessage || 'Tu solicitud de retiro fue rechazada.',
      };
    }
    return { ok: true };
  };
